/**
 * Users list state that lives in the URL (?page=2&search=gmail&sortBy=name…).
 * Every value is checked against the API's whitelist; anything unknown falls back
 * to its default so a hand-edited or stale URL can never produce a 400.
 */

export const LIMIT_OPTIONS = [10, 20, 50];
export const STATUSES = ['all', 'active', 'inactive'];
export const FUEL_TYPES = ['benzine', 'diesel', 'electric', 'hybrid', 'lpg'];
export const SORT_FIELDS = ['createdAt', 'name', 'email', 'scansMade'];
export const SORT_ORDERS = ['asc', 'desc'];
export const MIN_SEARCH_LENGTH = 2;
const MAX_LIMIT = 100;
const MAX_SEARCH_LENGTH = 100;

/**
 * @typedef {object} UsersParams
 * @property {number} page        ≥ 1
 * @property {number} limit       1–100
 * @property {string} search      trimmed; '' = none
 * @property {'all'|'active'|'inactive'} status
 * @property {''|'benzine'|'diesel'|'electric'|'hybrid'|'lpg'} fuelType  '' = any
 * @property {'createdAt'|'name'|'email'|'scansMade'} sortBy
 * @property {'asc'|'desc'} sortOrder
 */

/** @type {UsersParams} */
export const DEFAULT_PARAMS = Object.freeze({
  page: 1,
  limit: 20,
  search: '',
  status: 'all',
  fuelType: '',
  sortBy: 'createdAt',
  sortOrder: 'desc',
});

function parseIntInRange(value, min, max) {
  if (value == null || !/^\d+$/.test(value)) return null;
  const n = Number(value);
  return n >= min && n <= max ? n : null;
}

function oneOf(value, allowed) {
  return allowed.includes(value) ? value : null;
}

/**
 * @param {URLSearchParams} searchParams
 * @returns {{ params: UsersParams, invalid: string[] }} `invalid` lists params that were
 *   present but rejected, so the caller can clean the URL.
 */
export function parseUsersParams(searchParams) {
  const invalid = [];
  const pick = (key, parsed) => {
    const raw = searchParams.get(key);
    if (raw === null) return DEFAULT_PARAMS[key];
    if (parsed === null) {
      invalid.push(key);
      return DEFAULT_PARAMS[key];
    }
    return parsed;
  };

  const rawSearch = searchParams.get('search');
  const search = rawSearch === null ? '' : rawSearch.trim().slice(0, MAX_SEARCH_LENGTH);
  if (rawSearch !== null && search.length < MIN_SEARCH_LENGTH) invalid.push('search');

  const params = {
    page: pick('page', parseIntInRange(searchParams.get('page'), 1, Number.MAX_SAFE_INTEGER)),
    limit: pick('limit', parseIntInRange(searchParams.get('limit'), 1, MAX_LIMIT)),
    search: search.length >= MIN_SEARCH_LENGTH ? search : '',
    status: pick('status', oneOf(searchParams.get('status'), STATUSES)),
    fuelType: pick('fuelType', oneOf(searchParams.get('fuelType'), FUEL_TYPES)),
    sortBy: pick('sortBy', oneOf(searchParams.get('sortBy'), SORT_FIELDS)),
    sortOrder: pick('sortOrder', oneOf(searchParams.get('sortOrder'), SORT_ORDERS)),
  };
  return { params, invalid };
}

/**
 * Serialises params with URLSearchParams (so values are always encoded).
 * For the URL, defaults are left out to keep it short; the API gets page/limit/sort explicitly.
 */
export function toQueryString(params, { includeDefaults = false } = {}) {
  const qs = new URLSearchParams();
  const add = (key, value) => {
    if (value === '' || value == null) return;
    if (!includeDefaults && value === DEFAULT_PARAMS[key]) return;
    qs.set(key, String(value));
  };
  add('page', params.page);
  add('limit', params.limit);
  add('search', params.search);
  // "all" is the API default, so there's no need to send it
  if (params.status !== 'all') add('status', params.status);
  add('fuelType', params.fuelType);
  add('sortBy', params.sortBy);
  add('sortOrder', params.sortOrder);
  return qs.toString();
}

/**
 * Applies a change and resets to page 1 whenever anything other than the page itself
 * changes (limit, search, filters, sort), as the list it pointed into no longer exists.
 */
export function updateParams(current, changes) {
  const next = { ...current, ...changes };
  const onlyPageChanged = Object.keys(changes).every((k) => k === 'page');
  if (!onlyPageChanged) next.page = 'page' in changes ? changes.page : 1;
  return next;
}

/** Header click: same column flips the order; a new column starts A→Z (or most scans first). */
export function nextSort(current, field) {
  if (current.sortBy === field) return { sortBy: field, sortOrder: current.sortOrder === 'asc' ? 'desc' : 'asc' };
  return { sortBy: field, sortOrder: field === 'scansMade' ? 'desc' : 'asc' };
}
